<!-- 
Status timeline for a levy collection.

Shows the different statuses that the levy collection has moved through and the statuses 
that it hasn't yet reached. If the number of statuses gets too large (e.g. if the levy collection
moved back to a previous status at some point) then it will switch to being a button that will then
bring up a dialog showing the status timeline.
-->
<template>
  <div v-if="hasData">
    <status-timeline-stepper 
      v-if="!tooBig" 
      :history="history" 
      :status-descriptions="statusDescriptions"
      :transparent="transparent" 
      class="main-step hidden-md-and-down"
    />
    <div :class="tooBig ? '' : 'hidden-lg-and-up'" class="ml-2">
      <v-btn flat color="primary" class="ml-0" @click="showDialog = true">
        <v-icon class="mr-2">timeline</v-icon>
        Show status timeline
      </v-btn>
    </div>
    <v-dialog v-model="showDialog" persistent content-class="dialog xs">
      <v-card>
        <v-toolbar color="secondary" flat dark>
          <v-toolbar-title>Status timeline</v-toolbar-title>
        </v-toolbar>
        <v-card-text class="text-xs-center pa-0">
          <div class="scroll-container">
            <status-timeline-stepper 
              :history="history" 
              :status-descriptions="statusDescriptions"
              :transparent="transparent" 
              vertical
            />
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn flat @click="showDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import lookupMixins from "@/mixins/lookup.mixins";
import StatusTimelineStepper from "./StatusTimelineStepper.vue";
import * as lcpAction from "@/store/levy-collection-providers/action-types";
import * as lcAction from "@/store/levy-collections/action-types";
import { deepCopy } from "@/store/helpers";
import _ from "underscore";

export default {
  components: {
    StatusTimelineStepper
  },
  mixins: [lookupMixins],
  props: {
    levyCollectionId: {
      type: Number,
      required: true
    },
    providerId: {
      type: Number,
      default: null
    },
    transparent: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showDialog: false
    };
  },
  computed: {
    state() {
      if (this.hasProviderId) {
        return this.$store.state.levyCollectionProviders.statusHistory;
      } else {
        return this.$store.state.levyCollections.statusHistory;
      }
    },
    loaded() {
      return this.state.loaded;
    },
    hasData() {
      return !!this.state.data.length;
    },
    history() {
      const h = deepCopy(this.state.data);
      _(h).each(s => (s.current = false));
      _(h).last().current = true;
      const currentStatus = _(h).last().status;
      const index = _(this.statuses).findIndex(s => s.value == currentStatus);
      for (let i = index + 1; i < this.statuses.length; i++) {
        h.push({ status: this.statuses[i].value, date: null, user: null, userId: null, current: false });
      }
      return h;
    },
    tooBig() {
      return this.history.length > 6;
    },
    statusDescriptions() {
      return this.hasProviderId
        ? this.lookups.levyCollectionProviderStatusDescriptions
        : this.lookups.levyCollectionStatusDescriptions;
    },
    statuses() {
      return this.hasProviderId ? this.lookups.levyCollectionProviderStatuses : this.lookups.levyCollectionStatuses;
    },
    hasProviderId() {
      return !!this.providerId;
    }
  },
  watch: {
    loaded() {
      this.reload();
    },
    levyCollectionId() {
      this.reload();
    },
    providerId() {
      this.reload();
    }
  },
  mounted() {
    this.reload();
  },
  methods: {
    reload() {
      const notLoaded = !this.state.loaded && !this.state.loading;
      if (this.hasProviderId) {
        const reloadRequired =
          notLoaded ||
          (this.state.levyCollectionId != this.levyCollectionId || this.state.providerId != this.providerId);
        if (reloadRequired) {
          this.$store.dispatch(lcpAction.GET_STATUS_HISTORY, {
            levyCollectionId: this.levyCollectionId,
            providerId: this.providerId
          });
        }
      } else {
        const reloadRequired = notLoaded || this.state.levyCollectionId != this.levyCollectionId;
        if (reloadRequired) {
          this.$store.dispatch(lcAction.GET_STATUS_HISTORY, this.levyCollectionId);
        }
      }
    }
  }
};
</script>

<style lang="scss">
.main-step {
  .v-stepper__step {
    padding: 16px;
  }
  .v-stepper__header .v-divider {
    margin: 0;
  }
}
</style>
